<template>
  <div
    class="header-city"
    :class="`header-city--${variant}`"
  >
    <button
      v-if="isVisible"
      type="button"
      class="header-city__chip"
      :class="{ 'header-city__chip--active': isChipHighlighted }"
      :aria-label="chipAriaLabel"
      @click="handleChipClick"
    >
      <SvgoGeoSend class="header-city__icon" aria-hidden="true" />
      <span class="header-city__name">
        {{ displayText }}
      </span>
    </button>

    <Teleport to="body">
      <HeaderCityPopover
        v-if="showCityPopover && isMobileToolbarPopover"
        mobile-anchor="burger"
        :style="mobilePopoverStyle"
        :city-name="cityStore.prepositionalName ?? ''"
        @confirm="cityStore.confirmCity()"
        @change="handleChangeCity"
      />
    </Teleport>

    <HeaderCityPopover
      v-if="showCityPopover && !isMobileToolbarPopover"
      :alignment="variant === 'menu' ? 'start' : 'end'"
      :city-name="cityStore.prepositionalName ?? ''"
      @confirm="cityStore.confirmCity()"
      @change="handleChangeCity"
    />
  </div>
</template>

<script setup lang="ts">
import { useCityStore } from '~/stores/city'

const props = withDefaults(
  defineProps<{
    variant?: 'toolbar' | 'menu'
  }>(),
  { variant: 'toolbar' },
)

const cityStore = useCityStore()
const headerMobileMenuOpen = inject<Ref<boolean>>('headerMobileMenuOpen', ref(false))
const isMounted = ref(false)

/** Попап всегда в toolbar: на мобиле чип скрыт, попап — у бургера. */
const shouldHostPopover = computed(() => props.variant === 'toolbar')
const isMobileLayout = useHeaderMobileLayout()
const isMobileToolbarPopover = computed(
  () => props.variant === 'toolbar' && isMobileLayout.value,
)
const showCityPopover = computed(
  () =>
    isMounted.value
    && shouldHostPopover.value
    && cityStore.status === 'suggested'
    && !cityStore.sidebarOpen,
)

const popoverShiftX = ref(0)
const burgerAnchorTop = ref(0)
const burgerAnchorCenterX = ref(0)

const mobilePopoverStyle = computed(() => ({
  '--city-popover-top': `${burgerAnchorTop.value}px`,
  '--city-popover-left': `${burgerAnchorCenterX.value}px`,
  '--city-popover-shift-x': `${popoverShiftX.value}px`,
}))

function updateMobilePopoverPosition() {
  if (!import.meta.client || !showCityPopover.value || !isMobileToolbarPopover.value) {
    popoverShiftX.value = 0
    return
  }

  const burger = document.querySelector('.header-burger') as HTMLElement | null
  const popover = document.querySelector('.city-popover--mobile-burger') as HTMLElement | null
  if (!burger) {
    popoverShiftX.value = 0
    return
  }

  const burgerRect = burger.getBoundingClientRect()
  burgerAnchorTop.value = burgerRect.bottom + 6
  burgerAnchorCenterX.value = burgerRect.left + burgerRect.width / 2

  const popoverWidth = popover?.getBoundingClientRect().width ?? 272
  const minCenterX = 16 + popoverWidth / 2
  const maxCenterX = window.innerWidth - 16 - popoverWidth / 2
  const clampedCenterX = Math.min(
    Math.max(burgerAnchorCenterX.value, minCenterX),
    maxCenterX,
  )
  popoverShiftX.value = clampedCenterX - burgerAnchorCenterX.value
}

watch(headerMobileMenuOpen, async () => {
  await nextTick()
  updateMobilePopoverPosition()
})

watch([showCityPopover, isMobileToolbarPopover], async () => {
  await nextTick()
  updateMobilePopoverPosition()
  await nextTick()
  updateMobilePopoverPosition()
}, { immediate: true })

onMounted(async () => {
  window.addEventListener('resize', updateMobilePopoverPosition, { passive: true })
  window.addEventListener('scroll', updateMobilePopoverPosition, { passive: true, capture: true })

  await cityStore.syncFromLocalStorage()
  isMounted.value = true

  if (cityStore.status === 'pending') {
    await cityStore.init()
  }

  await nextTick()
  updateMobilePopoverPosition()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobilePopoverPosition)
  window.removeEventListener('scroll', updateMobilePopoverPosition, { capture: true })
})

const isVisible = computed(() =>
  cityStore.status !== 'pending',
)

const isChipHighlighted = computed(
  () =>
    isMounted.value
    && props.variant === 'toolbar'
    && cityStore.status === 'suggested'
    && !cityStore.sidebarOpen,
)

const displayText = computed(() => {
  if (cityStore.status === 'unknown' || !cityStore.cityName) {
    return 'Выберите город'
  }
  return cityStore.cityName
})

const chipAriaLabel = computed(() => {
  if (cityStore.status === 'unknown') {
    return 'Выберите город'
  }
  if (cityStore.status === 'suggested') {
    return `Город: ${cityStore.cityName}. Нажмите для подтверждения или смены`
  }
  return `Город: ${cityStore.cityName}. Нажмите чтобы изменить`
})

const handleChipClick = () => {
  if (cityStore.status === 'suggested') {
    return
  }
  cityStore.openSidebar()
}

const handleChangeCity = () => {
  cityStore.openSidebar()
}
</script>

<style lang="scss">
.header-city {
  position: relative;

  &--toolbar {
    @include media(header-nav, 'min-width') {
      display: flex;
      flex: 0 0 auto;
      align-items: center;

      .header-city__chip {
        gap: 6px;
        padding-inline: 8px 10px;
      }
    }

    @include media(header-nav) {
      .header-city__chip {
        display: none;
      }
    }

    .header-city__chip {
      background-color: transparent;

      &:hover,
      &--active {
        background-color: alpha($color-black, 0.06);
      }

      &:focus-visible {
        background-color: alpha($color-black, 0.06);
        outline: 2px solid $color-purple;
        outline-offset: 2px;
      }
    }
  }

  &--menu {
    display: none;

    @include media(header-nav) {
      display: block;
    }

    .header-city__chip {
      width: 100%;
      justify-content: flex-start;
      padding: 12px;
      border-radius: 8px;
      margin-left: 0;
      min-width: 0;
    }

    .header-city__icon {
      width: 24px;
      height: 24px;
    }

    .header-city__name {
      font-size: 14px;
      font-weight: 400;
      color: $color-black;
      line-height: 1.35;
    }

    .header-city__chip:hover {
      background-color: transparent;
    }

    .header-city__chip:hover .header-city__name {
      color: $color-accent-text;
    }
  }

  &__chip {
    appearance: none;
    border: none;
    cursor: pointer;
    font: inherit;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px 12px 16px;
    border-radius: 24px;
    white-space: nowrap;
    min-width: 56px;
    transition: background-color 0.15s ease;

    @include media(lg) {
      padding: 10px 14px 10px 12px;
      gap: 6px;
    }

    @include media(md) {
      padding: 8px 10px 8px 8px;
    }
  }

  &__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: $color-accent-text;
  }

  &__name {
    font-size: 14px;
    line-height: 1.35;
    color: $color-black;
  }
}
</style>
